import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ChecklistSession from "@/pages/ChecklistSession";
import Library from "@/pages/Library";
import Templates from "@/pages/admin/Templates";
import UsersPage from "@/pages/admin/Users";
import SettingsPage from "@/pages/admin/Settings";
import ActivityLog from "@/pages/admin/ActivityLog";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
      <Loader2 className="animate-spin" size={22} />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function EmployeeBlocked({ children }) {
  const { user } = useAuth();
  if (user?.role === "employee") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/sessions/:id" element={<Protected><ChecklistSession /></Protected>} />
      <Route path="/library" element={<Protected><EmployeeBlocked><Library /></EmployeeBlocked></Protected>} />
      <Route path="/admin/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/admin/users" element={<Protected><UsersPage /></Protected>} />
      <Route path="/admin/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/admin/activity" element={<Protected><ActivityLog /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
